
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { InfoIcon, CheckCircle, AlertCircle, XCircle } from 'lucide-react';

export interface ScoreCardProps {
  title: string;
  score: number;
  maxScore?: number;
  description?: string;
}

export const ScoreCard: React.FC<ScoreCardProps> = ({
  title,
  score,
  maxScore = 100,
  description
}) => {
  const [animatedWidth, setAnimatedWidth] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  
  // Calculate percentage for the progress bar
  const percentage = Math.round((score / maxScore) * 100);
  
  // Animate progress bar on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedWidth(percentage);
    }, 200);
    return () => clearTimeout(timer);
  }, [percentage]);
  
  // Determine color and styling based on score percentage
  const getScoreLevel = () => {
    if (percentage >= 80) return {
      color: 'text-emerald-500',
      bg: 'bg-emerald-500',
      gradient: 'from-emerald-500/10 to-green-500/5',
      icon: CheckCircle,
      level: 'Excellent'
    };
    if (percentage >= 60) return {
      color: 'text-lime-500',
      bg: 'bg-lime-500',
      gradient: 'from-lime-500/10 to-green-500/5',
      icon: CheckCircle,
      level: 'Good'
    };
    if (percentage >= 40) return {
      color: 'text-yellow-500',
      bg: 'bg-yellow-500',
      gradient: 'from-yellow-500/10 to-amber-500/5',
      icon: AlertCircle,
      level: 'Fair'
    };
    if (percentage >= 20) return {
      color: 'text-orange-500',
      bg: 'bg-orange-500',
      gradient: 'from-orange-500/10 to-red-500/5',
      icon: AlertCircle,
      level: 'Poor'
    };
    return {
      color: 'text-red-500',
      bg: 'bg-red-500',
      gradient: 'from-red-500/10 to-red-600/5',
      icon: XCircle,
      level: 'Critical'
    };
  };

  const scoreLevel = getScoreLevel();
  const Icon = scoreLevel.icon;

  return (
    <Card 
      className="overflow-hidden border border-border/50 hover:border-border transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardContent className="p-3 sm:p-4">
        <div className="flex justify-between items-start mb-2 sm:mb-3">
          <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
            <Icon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${scoreLevel.color} transition-transform duration-300 ${isHovered ? 'scale-110' : ''} shrink-0`} />
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-xs sm:text-sm mb-1 truncate">{title}</h3>
              <div className={`inline-block px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs bg-gradient-to-r ${scoreLevel.gradient} border border-current/10`}>
                {scoreLevel.level}
              </div>
            </div>
            {description && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <InfoIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground cursor-help hover:text-foreground transition-colors shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs text-xs">
                  {description}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
        
        <div className="flex items-center justify-between mb-2">
          <span className={`text-base sm:text-lg font-bold ${scoreLevel.color}`}>
            {score}
          </span>
          <span className="text-xs sm:text-sm text-muted-foreground">
            / {maxScore}
          </span>
        </div>
        
        <div className="relative">
          <div className="w-full h-1.5 sm:h-2 bg-muted/30 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-1000 ease-out ${scoreLevel.bg} rounded-full`}
              style={{ width: `${animatedWidth}%` }}
            />
          </div>
          <div 
            className="absolute top-0 left-0 h-1.5 sm:h-2 bg-white/20 rounded-full transition-all duration-1000 ease-out"
            style={{ width: `${animatedWidth}%` }}
          />
        </div>
        
        <div className="text-right mt-1">
          <span className={`text-xs font-medium ${scoreLevel.color}`}>
            {percentage}%
          </span>
        </div>
      </CardContent>
    </Card>
  );
};
